import React, { useState, useEffect } from 'react';

const StatusBadge = ({ status }) => {
  const map = {
    completed: { color: '#34D399', bg: 'rgba(52,211,153,0.12)', label: 'Tamamlandı' },
    missed:    { color: '#F87171', bg: 'rgba(248,113,113,0.12)', label: 'Cevapsız' },
    redirected:{ color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  label: 'Yönlendirildi' },
    active:    { color: '#60A5FA', bg: 'rgba(96,165,250,0.12)',  label: 'Aktif' }
  };
  const s = map[status] || { color: '#9CA3AF', bg: 'rgba(156,163,175,0.12)', label: status };
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
      padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 600
    }}>{s.label}</span>
  );
};

const SentimentBadge = ({ sentiment }) => {
  const map = { positive: '😊', neutral: '😐', negative: '😞' };
  return <span style={{ fontSize: 18 }}>{map[sentiment] || '😐'}</span>;
};

const ReasonBadge = ({ reason }) => {
  if (!reason) return null;
  return (
    <span style={{
      background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.25)',
      padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap'
    }}>📋 {reason}</span>
  );
};

const LeadBadge = ({ priority }) => {
  const map = {
    priority: { color: '#F87171', bg: 'rgba(248,113,113,0.12)', label: '🔴 Öncelikli' },
    interested:{ color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', label: '🔵 İlgili' },
    follow_up: { color: '#FBBF24', bg: 'rgba(251,191,36,0.12)',  label: '🟡 Takipte' }
  };
  const s = map[priority];
  if (!s) return null;
  return (
    <span style={{
      background: s.bg, color: s.color, border: `1px solid ${s.color}33`,
      padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600
    }}>{s.label}</span>
  );
};

// apiBase/token: yonetim panelindeki AI Center bu bilesenleri DEGISTIRMEDEN
// yeniden kullanabilsin diye opsiyonel - verilmezse tenant'taki mevcut
// davranis (kendi crm_token'i + /api/callops) AYNEN korunur.
const CallsTab = ({ apiBase = '/api/callops', token } = {}) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: 'all', sentiment: 'all', search: '' });
  const [selectedCall, setSelectedCall] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState('');
  const authToken = token || localStorage.getItem('crm_token');

  useEffect(() => { fetchCalls(); }, [filters]);

  useEffect(() => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null); setAudioError(''); setAudioLoading(false);
  }, [selectedCall]);

  const loadCallAudio = async () => {
    if (!selectedCall) return;
    setAudioLoading(true); setAudioError('');
    try {
      const res = await fetch(`${apiBase}/calls/${selectedCall.id}/audio`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Ses kaydı alınamadı'); }
      setAudioUrl(URL.createObjectURL(await res.blob()));
    } catch (err) { setAudioError(err.message); }
    setAudioLoading(false);
  };

  const fetchCalls = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.sentiment !== 'all') params.append('sentiment', filters.sentiment);
      const res = await fetch(`${apiBase}/calls?${params}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      const data = await res.json();
      setCalls(data.calls || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const formatDuration = (s) => { if (!s) return '-'; return `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`; };
  const formatDate = (d) => new Date(d).toLocaleString('tr-TR');
  const flags = { TR:'🇹🇷', US:'🇺🇸', DE:'🇩🇪', GB:'🇬🇧', SA:'🇸🇦', FR:'🇫🇷', IT:'🇮🇹' };

  const filteredCalls = calls.filter(c => {
    if (!filters.search) return true;
    const s = filters.search.toLowerCase();
    return c.caller_name?.toLowerCase().includes(s) || c.caller_phone?.includes(s);
  });

  const summaryStats = [
    { label: 'Toplam', value: calls.length, color: '#E5E7EB' },
    { label: 'Tamamlandı', value: calls.filter(c => c.status === 'completed').length, color: '#34D399' },
    { label: 'Cevapsız', value: calls.filter(c => c.status === 'missed').length, color: '#F87171' },
    { label: 'Yönlendirildi', value: calls.filter(c => c.status === 'redirected').length, color: '#FBBF24' }
  ];

  return (
    <div className="calls-tab">
      {/* Header */}
      <div className="calls-header">
        <div>
          <h2>📞 Aramalar</h2>
          <p style={{ margin:0, color:'#6B7280', fontSize:13 }}>{filteredCalls.length} kayıt</p>
        </div>
        <button className="btn-refresh" onClick={fetchCalls}>
          <span style={{ fontSize:14 }}>🔄</span> Yenile
        </button>
      </div>

      {/* Stats */}
      <div className="calls-stats-row">
        {summaryStats.map(s => (
          <div key={s.label} className="call-stat-card">
            <div className="call-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="call-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="filters-row">
        <div className="search-wrap">
          <span className="search-icon">🔍</span>
          <input
            type="text" className="search-input"
            placeholder="İsim veya telefon ara..."
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
          />
        </div>
        <select className="filter-select" value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="all">Tüm Durumlar</option>
          <option value="completed">Tamamlandı</option>
          <option value="missed">Cevapsız</option>
          <option value="redirected">Yönlendirildi</option>
        </select>
        <select className="filter-select" value={filters.sentiment} onChange={e => setFilters({ ...filters, sentiment: e.target.value })}>
          <option value="all">Tüm Duygular</option>
          <option value="positive">😊 Pozitif</option>
          <option value="neutral">😐 Nötr</option>
          <option value="negative">😞 Negatif</option>
        </select>
      </div>

      {/* Table */}
      <div className="calls-table-wrap">
        <table className="calls-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}></th>
              <th>Arayan</th>
              <th>Sebep</th>
              <th>Tarih</th>
              <th>Süre</th>
              <th>Durum</th>
              <th>Duygu</th>
              <th>Lead</th>
              <th>Sektör</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="table-empty"><div className="mini-spinner" /> Yükleniyor...</td></tr>
            ) : filteredCalls.length === 0 ? (
              <tr><td colSpan="9" className="table-empty">🔍 Sonuç bulunamadı</td></tr>
            ) : filteredCalls.map(call => (
              <tr key={call.id} className="calls-row" onClick={() => setSelectedCall(call)}>
                <td style={{ textAlign: 'center', fontSize: 18 }}>{flags[call.caller_country] || '🌍'}</td>
                <td>
                  <div className="caller-name">{call.caller_name || '-'}</div>
                  <div className="caller-phone">{call.caller_phone}</div>
                </td>
                <td>{call.call_reason ? <ReasonBadge reason={call.call_reason} /> : <span className="cell-muted">-</span>}</td>
                <td className="cell-muted">{formatDate(call.created_at)}</td>
                <td className="cell-mono">{formatDuration(call.duration_seconds)}</td>
                <td><StatusBadge status={call.status} /></td>
                <td><SentimentBadge sentiment={call.sentiment} /></td>
                <td>{call.lead_priority && <LeadBadge priority={call.lead_priority} />}</td>
                <td className="cell-muted">{call.sector || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {selectedCall && (
        <div className="modal-overlay" onClick={() => setSelectedCall(null)}>
          <div className="calls-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-hdr">
              <div>
                <h3 style={{ margin:0, color:'#F9FAFB' }}>📞 Arama Detayı</h3>
                <div style={{ fontSize:13, color:'#6B7280', marginTop:2 }}>{formatDate(selectedCall.created_at)}</div>
              </div>
              <button className="modal-x" onClick={() => setSelectedCall(null)}>×</button>
            </div>
            <div className="modal-bdy">
              <div className="detail-grid-2">
                <div className="detail-cell"><label>Arayan</label><div className="detail-val">{selectedCall.caller_name || '-'}</div><div className="detail-sub">{selectedCall.caller_phone}</div></div>
                <div className="detail-cell"><label>Süre</label><div className="detail-val mono">{formatDuration(selectedCall.duration_seconds)}</div></div>
                <div className="detail-cell"><label>Durum</label><div style={{ marginTop:4 }}><StatusBadge status={selectedCall.status} /></div></div>
                <div className="detail-cell"><label>Duygu</label><div className="detail-val"><SentimentBadge sentiment={selectedCall.sentiment} /> {selectedCall.sentiment}</div></div>
                <div className="detail-cell"><label>Sektör</label><div className="detail-val">{selectedCall.sector || '-'}</div></div>
                {selectedCall.call_reason && (
                  <div className="detail-cell"><label>Arama Sebebi</label><div style={{ marginTop: 4 }}><ReasonBadge reason={selectedCall.call_reason} /></div></div>
                )}
              </div>

              {selectedCall.status === 'completed' && (
                <div className="audio-box">
                  <label>🎙️ Ses Kaydı</label>
                  {audioUrl ? (
                    <audio controls autoPlay src={audioUrl} style={{ width: '100%', marginTop: 8 }} />
                  ) : (
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <button className="btn-audio" onClick={loadCallAudio} disabled={audioLoading}>
                        {audioLoading ? '⏳ Yükleniyor...' : '▶ Dinle'}
                      </button>
                      {audioError && <span style={{ fontSize: 12, color: '#F87171' }}>{audioError}</span>}
                    </div>
                  )}
                </div>
              )}

              {selectedCall.ai_summary && (
                <div className="info-box blue">
                  <label>🤖 AI Özeti</label>
                  <div>{selectedCall.ai_summary}</div>
                </div>
              )}

              {selectedCall.transcript && (
                <div className="transcript-box">
                  <label>📄 Transkript</label>
                  <pre>{selectedCall.transcript}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .calls-tab { display:flex; flex-direction:column; gap:20px; }

        .calls-header { display:flex; justify-content:space-between; align-items:flex-start; }
        .calls-header h2 { margin:0 0 2px; font-size:22px; font-weight:700; color:#F9FAFB; }
        .btn-refresh { display:flex; align-items:center; gap:8px; padding:10px 18px; background:rgba(255,255,255,0.06); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; cursor:pointer; transition:all 0.2s; }
        .btn-refresh:hover { background:rgba(255,255,255,0.1); }

        .calls-stats-row { display:grid; grid-template-columns:repeat(4,1fr); gap:12px; }
        .call-stat-card { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:16px 20px; }
        .call-stat-value { font-size:28px; font-weight:800; margin-bottom:2px; }
        .call-stat-label { font-size:12px; color:#6B7280; text-transform:uppercase; letter-spacing:0.04em; }

        .filters-row { display:flex; gap:12px; flex-wrap:wrap; }
        .search-wrap { position:relative; flex:1; min-width:200px; }
        .search-icon { position:absolute; left:12px; top:50%; transform:translateY(-50%); font-size:14px; }
        .search-input { width:100%; padding:10px 14px 10px 36px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; transition:border-color 0.2s; box-sizing:border-box; }
        .search-input:focus { border-color:rgba(96,165,250,0.4); }
        .filter-select { padding:10px 14px; background:rgba(255,255,255,0.05); border:1px solid rgba(255,255,255,0.08); border-radius:10px; color:#E5E7EB; font-size:14px; outline:none; cursor:pointer; }
        .filter-select option { background:#13131a; }

        .calls-table-wrap { background:rgba(17,17,24,0.7); border:1px solid rgba(255,255,255,0.07); border-radius:16px; overflow:hidden; backdrop-filter:blur(16px); }
        .calls-table { width:100%; border-collapse:collapse; }
        .calls-table thead th { padding:12px 16px; text-align:left; font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#6B7280; font-weight:700; background:rgba(255,255,255,0.03); border-bottom:1px solid rgba(255,255,255,0.06); }
        .calls-row { cursor:pointer; transition:background 0.15s; }
        .calls-row:hover { background:rgba(255,255,255,0.04); }
        .calls-row td { padding:14px 16px; border-bottom:1px solid rgba(255,255,255,0.04); vertical-align:middle; }
        .calls-row:last-child td { border-bottom:none; }
        .caller-name { font-size:14px; font-weight:600; color:#E5E7EB; }
        .caller-phone { font-size:12px; color:#6B7280; margin-top:1px; }
        .cell-muted { font-size:13px; color:#6B7280; }
        .cell-mono { font-family:monospace; font-size:13px; color:#E5E7EB; }
        .table-empty { text-align:center; padding:48px; color:#6B7280; font-size:14px; }
        .mini-spinner { display:inline-block; width:16px; height:16px; border:2px solid rgba(255,255,255,0.1); border-top-color:#60A5FA; border-radius:50%; animation:spin 0.8s linear infinite; margin-right:8px; vertical-align:middle; }
        @keyframes spin { to { transform:rotate(360deg); } }

        /* Modal */
        .modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.7); backdrop-filter:blur(8px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:20px; }
        .calls-modal { background:#0f0f16; border:1px solid rgba(255,255,255,0.1); border-radius:20px; width:100%; max-width:620px; max-height:85vh; overflow:hidden; display:flex; flex-direction:column; box-shadow:0 24px 60px rgba(0,0,0,0.6); }
        .modal-hdr { display:flex; justify-content:space-between; align-items:flex-start; padding:20px 24px; border-bottom:1px solid rgba(255,255,255,0.07); }
        .modal-x { background:none; border:none; font-size:28px; color:#6B7280; cursor:pointer; line-height:1; transition:color 0.15s; }
        .modal-x:hover { color:#F87171; }
        .modal-bdy { padding:24px; overflow-y:auto; display:flex; flex-direction:column; gap:16px; }

        .detail-grid-2 { display:grid; grid-template-columns:repeat(2,1fr); gap:12px; }
        .detail-cell { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.06); border-radius:10px; padding:12px 14px; }
        .detail-cell label { font-size:11px; text-transform:uppercase; letter-spacing:0.06em; color:#6B7280; display:block; margin-bottom:6px; }
        .detail-val { font-size:15px; font-weight:600; color:#E5E7EB; }
        .detail-sub { font-size:12px; color:#6B7280; margin-top:2px; }
        .detail-val.mono { font-family:monospace; }

        .audio-box { background:rgba(96,165,250,0.07); border:1px solid rgba(96,165,250,0.2); border-radius:12px; padding:16px; }
        .audio-box label { font-size:12px; font-weight:600; color:#60A5FA; }
        .btn-audio { padding:9px 20px; background:linear-gradient(135deg,#1d4ed8,#60A5FA); border:none; border-radius:25px; color:#fff; font-size:13px; font-weight:600; cursor:pointer; display:flex; align-items:center; gap:6px; }
        .btn-audio:disabled { opacity:0.6; cursor:default; }

        .info-box { border-radius:12px; padding:16px; }
        .info-box.blue { background:rgba(96,165,250,0.07); border:1px solid rgba(96,165,250,0.15); }
        .info-box label { font-size:12px; font-weight:600; color:#60A5FA; display:block; margin-bottom:8px; }
        .info-box div { font-size:14px; color:#D1D5DB; line-height:1.6; }

        .transcript-box { background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.07); border-radius:12px; padding:16px; }
        .transcript-box label { font-size:12px; font-weight:600; color:#9CA3AF; display:block; margin-bottom:8px; }
        .transcript-box pre { margin:0; white-space:pre-wrap; font-size:13px; color:#9CA3AF; line-height:1.6; font-family:inherit; }
      `}</style>
    </div>
  );
};

export default CallsTab;
